export const privacy = `**Privacy Policy**

Effective date: 05/13/2022

At Humming Homes, we take your privacy seriously. Please read this Privacy Policy to learn how we treat your personal data. **By using or accessing our Services in any manner, you acknowledge that you accept the practices and policies outlined below, and you hereby consent that we will collect, use and share your information as described in this Privacy Policy.**

Remember that your use of Humming Homes&#39; Services is at all times subject to our [Terms of Use](/terms);, which incorporates this Privacy Policy. Any terms we use in this Policy without defining them have the definitions given to them in the Terms of Use.

You may print a copy of this Privacy Policy by clicking the button available on the under this page's title;.

**Privacy Policy Table of Contents**

**What this Privacy Policy Covers**

**Personal Data**

  - _Categories of Personal Data We Collect_
  - _Categories of Sources of Personal Data_
  - _Our Commercial or Business Purposes for Collecting Personal Data_

**How We Share Your Personal Data**

**Tracking Tools and Opt-Out**

**Data Security and Retention**

**Personal Data of Children**

**Other State Law Privacy Rights**

**Changes to this Privacy Policy**

**Contact Information**

**What this Privacy Policy Covers**

This Privacy Policy covers how we treat Personal Data that we gather when you access or use our Services. &quot;Personal Data&quot; means any information that identifies or relates to a particular individual and also includes information referred to as &quot;personally identifiable information&quot; or &quot;personal information&quot; under applicable data privacy laws, rules or regulations. This Privacy Policy does not cover the practices of companies we don&#39;t own or control or people we don&#39;t manage.

## Personal Data 
### Categories of Personal Data We Collect

This chart details the categories of Personal Data that we collect and have collected over the past 12 months:

**Category of Personal Data: Profile or Contact Data**
- *Examples of Personal Data We Collect:*
  - First and last name
  - Email
  - Phone number
  - Mailing address
  - Home appliance data
  - Photos of the interior/exterior of home
  - Home occupants

- *Categories of Third Parties With Whom We Share this Personal Data:*
  - Service Providers
  - Analytics Partners

**Category of Personal Data: Online Identifiers**
- *Examples of Personal Data We Collect:*
  - Unique identifiers such as account name passwords
  
- *Categories of Third Parties With Whom We Share this Personal Data:*
  - We do not share this category of Personal Data with third parties

**Category of Personal Data: Identifiers**
- *Examples of Personal Data We Collect:*
  - Cultural or social identifiers (for example, being a skateboarder, a Green Bay Packers fan, an environmental activist, etc.)
  - Financial profiles (for example, credit card spend, assets)    
- *Categories of Third Parties With Whom We Share this Personal Data:*
  - Analytics Partners

**Category of Personal Data: Payment Data**
- *Examples of Personal Data We Collect:*
  - Payment card type
  - Last 4 digits of payment card
  - Bank account information
  - Billing address, phone number, and email
- *Categories of Third Parties With Whom We Share this Personal Data:*
  - Service Providers (specifically our payment processing partners, currently Plaid, Inc. and Stripe, Inc.)

**Category of Personal Data: Commercial Data**
- *Examples of Personal Data We Collect:*
  - Purchase history
  - Consumer profiles
- *Categories of Third Parties With Whom We Share this Personal Data:*
  - Business Partners

**Category of Personal Data: Device/IP Data**
- *Examples of Personal Data We Collect:*
  - IP address
  - Device ID
  - Domain server
  - Type of device/operating system/browser used to access the Services
- *Categories of Third Parties With Whom We Share this Personal Data:*
  - Analytics Partners

**Category of Personal Data: Web Analytics**
- *Examples of Personal Data We Collect:*
  - Web page interactions
  - Referring webpage/source through which you accessed the Services
  - Statistics associated with the interaction between device or browser and the Services
- *Categories of Third Parties With Whom We Share this Personal Data:*
  - Analytics Partners

**Category of Personal Data: Social Network Data**
- *Examples of Personal Data We Collect:*
  - Email
  - Phone number
  - Information from your social media profile
- *Categories of Third Parties With Whom We Share this Personal Data:*
  - We do not share this category of Personal Data with third parties

**Category of Personal Data: Consumer Demographic Data**
- *Examples of Personal Data We Collect:*
  - Age / date of birth
  - Zip code
  - Gender
- *Categories of Third Parties With Whom We Share this Personal Data:*
  - Advertising Partners

**Category of Personal Data: Geolocation Data**
- *Examples of Personal Data We Collect:*
  - IP-address-based location information
- *Categories of Third Parties With Whom We Share this Personal Data:*
  - We do not share this category of Personal Data with third parties

**Category of Personal Data: Sensory Data**
- *Examples of Personal Data We Collect:*
  - Photos, videos or recordings of your environment
- *Categories of Third Parties With Whom We Share this Personal Data:*
  - We do not share this category of Personal Data with third parties

_Categories of Sources of Personal Data_

We collect Personal Data about you from the following categories of sources:

- **You**
  - When you provide such information directly to us.
    - When you create an account or use our interactive tools and Services.
    - When you voluntarily provide information in free-form text boxes through the Services or through responses to surveys or questionnaires.
    - When you send us an email or otherwise contact us.
  - When you use the Services and such information is collected automatically.
    - Through Cookies (defined in the &quot;Tracking Tools and Opt-Out&quot; section below).
    - If you download our mobile application or use a location-enabled browser, we may receive information about your location and mobile device, as applicable.
    - If you download and install certain applications and software we make available, we may receive and collect information transmitted from your computing device for the purpose of providing you the relevant Services, such as information regarding when you are logged on and available to receive updates or alert notices.
- **Public Records**
  - From the government or other sources.
- **Third Parties**
  - Vendors
    - We may use analytics providers to analyze how you interact and engage with the Services, or third parties may help us provide you with customer support.
    - We may use vendors to obtain information to generate leads and create user profiles.
  - Advertising Partners
    - We receive information about you from some of our vendors who assist us with marketing or promotional services related to how you interact with our websites, applications, products, Services, advertisements or communications.

_Our Commercial or Business Purposes for Collecting Personal Data_

- **Providing, Customizing and Improving the Services**
  - Creating and managing your account or other user profiles.
  - Processing orders or other transactions; billing.
  - Providing you with the products, services or information you request.
  - Meeting or fulfilling the reason you provided the information to us.
  - Providing support and assistance for the Services.
  - Improving the Services, including testing, research, internal analytics and product development.
  - Personalizing the Services, website content and communications based on your preferences.
  - Doing fraud protection, security and debugging.
  - Carrying out other business purposes stated when collecting your Personal Data or as otherwise set forth in applicable data privacy laws, such as the California Consumer Privacy Act (the &quot;CCPA&quot;).
- **Marketing the Services**
  - Marketing and selling the Services.
- **Corresponding with You**
  - Responding to correspondence that we receive from you, contacting you when necessary or requested, and sending you information about Humming Homes or the Services.
  - Sending emails and other communications according to your preferences or that display content that we think will interest you.
- **Meeting Legal Requirements and Enforcing Legal Terms**
  - Fulfilling our legal obligations under applicable law, regulation, court order or other legal process, such as preventing, detecting and investigating security incidents and potentially illegal or prohibited activities.
  - Protecting the rights, property or safety of you, Humming Homes or another party.
  - Enforcing any agreements with you.
  - Responding to claims that any posting or other content violates third-party rights.
  - Resolving disputes.

We will not collect additional categories of Personal Data or use the Personal Data we collected for materially different, unrelated or incompatible purposes without providing you notice.

**How We Share Your Personal Data**

We disclose your Personal Data to the categories of service providers and other parties listed in this section. Depending on state laws that may be applicable to you, some of these disclosures may constitute a &quot;sale&quot; of your Personal Data. For more information, please refer to the state-specific sections below.

- **Service Providers.** These parties help us provide the Services or perform business functions on our behalf. They include:
  - Hosting, technology and communication providers.
  - Support and customer service vendors.
  - Product fulfillment and delivery providers.
  - Payment processors.
    - Our payment processing partners Plaid Inc. (&quot;Plaid&quot;) and Stripe, Inc. (&quot;Stripe&quot;) may collect your voluntarily-provided payment card information necessary to process your payment.
    - Please see Plaid and Stripe&#39;s respective terms of service and privacy policy for information on their respective use and storage of your Personal Data.
- **Advertising Partners.** These parties help us market our services and provide you with other offers that may be of interest to you. They include:
  - Ad networks.
  - Data brokers.
  - Marketing providers.
- **Analytics Partners.** These parties provide analytics on web traffic or usage of the Services. They include:
  - Companies that track how users found or were referred to the Services.
  - Companies that track how users interact with the Services.
- **Business Partners.** These parties partner with us in offering various services. They include:
  - Businesses that you have a relationship with.
  - Companies that we partner with to offer joint promotional offers or opportunities.
- **Parties You Authorize, Access or Authenticate**
  - Third parties you access through the services.

_Legal Obligations_

We may share any Personal Data that we collect with third parties in conjunction with any of the activities set forth under &quot;Meeting Legal Requirements and Enforcing Legal Terms&quot; in the &quot;Our Commercial or Business Purposes for Collecting Personal Data&quot; section above.

_Business Transfers_

All of your Personal Data that we collect may be transferred to a third party if we undergo a merger, acquisition, bankruptcy or other transaction in which that third party assumes control of our business (in whole or in part). Should one of these events occur, we will make reasonable efforts to notify you before your information becomes subject to different privacy and security policies and practices.

_Data that is Not Personal Data_

We may create aggregated, de-identified or anonymized data from the Personal Data we collect, including by removing information that makes the data personally identifiable to a particular user. We may use such aggregated, de-identified or anonymized data and share it with third parties for our lawful business purposes, including to analyze, build and improve the Services and promote our business, provided that we will not share such data in a manner that could identify you.

**Tracking Tools and Opt-Out**

The Services use cookies and similar technologies such as pixel tags, web beacons, clear GIFs and JavaScript (collectively, &quot;Cookies&quot;) to enable our servers to recognize your web browser, tell us how and when you visit and use our Services, analyze trends, learn about our user base and operate and improve our Services. Cookies are small pieces of data– usually text files – placed on your computer, tablet, phone or similar device when you use that device to access our Services. We may also supplement the information we collect from you with information received from third parties, including third parties that have placed their own Cookies on your device(s). Please note that because of our use of Cookies, the Services do not support &quot;Do Not Track&quot; requests sent from a browser at this time.

We use the following types of Cookies:

- Essential Cookies. Essential Cookies are required for providing you with features or services that you have requested. For example, certain Cookies enable you to log into secure areas of our Services. Disabling these Cookies may make certain features and services unavailable.
- Functional Cookies. Functional Cookies are used to record your choices and settings regarding our Services, maintain your preferences over time and recognize you when you return to our Services. These Cookies help us to personalize our content for you, greet you by name and remember your preferences (for example, your choice of language or region).
- Performance/Analytical Cookies. Performance/Analytical Cookies allow us to understand how visitors use our Services. They do this by collecting information about the number of visitors to the Services, what pages visitors view on our Services and how long visitors are viewing pages on the Services. Performance/Analytical Cookies also help us measure the performance of our advertising campaigns in order to help us improve our campaigns and the Services&#39; content for those who engage with our advertising. For example, Google LLC (&quot;Google&quot;) uses cookies in connection with its Google Analytics services. Google&#39;s ability to use and share information collected by Google Analytics about your visits to the Services is subject to the Google Analytics Terms of Use and the Google Privacy Policy. You have the option to opt-out of Google&#39;s use of Cookies by visiting the Google advertising opt-out page at www.google.com/privacy_ads.html or the Google Analytics Opt-out Browser Add-on at https://tools.google.com/dlpage/gaoptout/.

You can decide whether or not to accept Cookies through your internet browser&#39;s settings. Most browsers have an option for turning off the Cookie feature, which will prevent your browser from accepting new Cookies, as well as (depending on the sophistication of your browser software) allow you to decide on acceptance of each new Cookie in a variety of ways. You can also delete all Cookies that are already on your device. If you do this, however, you may have to manually adjust some preferences every time you visit our website and some of the Services and functionalities may not work.

To explore what Cookie settings are available to you, look in the &quot;preferences&quot; or &quot;options&quot; section of your browser&#39;s menu. To find out more information about Cookies, including information about how to manage and delete Cookies, please visit [http://www.allaboutcookies.org/](http://www.allaboutcookies.org/).

**Data Security and Retention**

We seek to protect your Personal Data from unauthorized access, use and disclosure using appropriate physical, technical, organizational and administrative security measures based on the type of Personal Data and how we are processing that data. You should also help protect your data by appropriately selecting and protecting your password and/or other sign-on mechanism; limiting access to your computer or device and browser; and signing off after you have finished accessing your account. Although we work to protect the security of your account and other data that we hold in our records, please be aware that no method of transmitting data over the internet or storing data is completely secure.

We retain Personal Data about you for as long as you have an open account with us or as otherwise necessary to provide you with our Services. In some cases we retain Personal Data for longer, if doing so is necessary to comply with our legal obligations, resolve disputes or collect fees owed, or is otherwise permitted or required by applicable law, rule or regulation. We may further retain information in an anonymous or aggregated form where that information would not identify you personally.

**Personal Data of Children**

As noted in the Terms of Use, we do not knowingly collect or solicit Personal Data about children under 13 years of age; if you are a child under the age of 13, please do not attempt to register for or otherwise use the Services or send us any Personal Data. If we learn we have collected Personal Data from a child under 13 years of age, we will delete that information as quickly as possible. If you believe that a child under 13 years of age may have provided Personal Data to us, please contact us at operations@humminghomes.com.

**Other State Law Privacy Rights**

_California Resident Rights_

Under California Civil Code Sections 1798.83-1798.84, California residents are entitled to contact us to prevent disclosure of Personal Data to third parties for such third parties&#39; direct marketing purposes; in order to submit such a request, please contact us at operations@humminghomes.com.

_Nevada Resident Rights_

If you are a resident of Nevada, you have the right to opt-out of the sale of certain Personal Data to third parties who intend to license or sell that Personal Data. You can exercise this right by contacting us at operations@humminghomes.com with the subject line &quot;Nevada Do Not Sell Request&quot; and providing us with your name and the email address associated with your account. Please note that we do not currently sell your Personal Data as sales are defined in Nevada Revised Statutes Chapter 603A

**Changes to this Privacy Policy**

We&#39;re constantly trying to improve our Services, so we may need to change this Privacy Policy from time to time, but we will alert you to any such changes by placing a notice on the Humming Homes website, by sending you an email and/or by some other means. Please note that if you&#39;ve opted not to receive legal notice emails from us (or you haven&#39;t provided us with your email address), those legal notices will still govern your use of the Services, and you are still responsible for reading and understanding them. If you use the Services after any changes to the Privacy Policy have been posted, that means you agree to all of the changes. Use of information we collect is subject to the Privacy Policy in effect at the time such information is collected.

**Contact Information:**

If you have any questions or comments about this Privacy Policy, the ways in which we collect and use your Personal Data or your choices and rights regarding such collection and use, please do not hesitate to contact us at:

- +1 833-775-1142
- humminghomes.com
- operations@humminghomes.com
- 20 W 22nd, STE 1411, New York, NY 10010`;
